<template>
  <ModalDialog
    ref="dialog"
    title="Printing Sheet Labels"
    icon="fa-print"
    sizeClass="modal-lg"
    :showFooter="true"
    closeTitle="Close"
    :showSubmit="false"
    @closed="onClosed">

    <LoadingPanel ref="loadingPanel" @update="loading = $event" />
    <div v-if="sheetData.id">
      <div v-if="hasPanels">
        <ContentTitle>Panels</ContentTitle>
        <FormRow label="Labels">
          <FormStaticLabel>{{panelCount}} Label{{displayCountPlural(panelCount)}}</FormStaticLabel>
        </FormRow>
        <FormRow label="Status">
          <FormStaticLabel v-if="isPrintingPanels"><i class="far fa-spinner-third fa-spin"></i> Printing...</FormStaticLabel>
          <FormStaticLabel v-if="isPrintingPanelComplete"><i class="far fa-check-circle"></i> Printed</FormStaticLabel>
        </FormRow>
        <FormRow label="Options">
          <FormStaticLabel>
            <FormButton class="btn-default" @click="cancelDialogTimer(); printPanels(false)" :disabled="!isPrintingPanelComplete"><i class="far fa-print"></i> Reprint</FormButton>
          </FormStaticLabel>
        </FormRow>
      </div>
      <FormHorizontalRule />
      <div v-if="hasOffcuts">
        <ContentTitle>Offcuts</ContentTitle>
        <FormRow label="Labels">
          <FormStaticLabel>{{offcutCount}} Label{{displayCountPlural(offcutCount)}}</FormStaticLabel>
        </FormRow>
        <FormRow label="Status">
          <FormStaticLabel v-if="!isPrintingOffcuts && !isPrintingOffcutComplete">Waiting</FormStaticLabel>
          <FormStaticLabel v-if="isPrintingOffcuts"><i class="far fa-spinner-third fa-spin"></i> Printing...</FormStaticLabel>
          <FormStaticLabel v-if="isPrintingOffcutComplete"><i class="far fa-check-circle"></i> Printed</FormStaticLabel>
        </FormRow>
        <FormRow label="Options">
          <FormStaticLabel>
            <FormButton class="btn-default" @click="cancelDialogTimer(); printOffcuts(false)" :disabled="!isPrintingOffcutComplete"><i class="far fa-print"></i> Reprint</FormButton>
          </FormStaticLabel>          
        </FormRow>
      </div>
    </div>
    
  </ModalDialog>
</template>

<style scoped>

</style>

<script>
import _ from 'lodash'

import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import modalDialog from '@/mixins/modalDialog'

import { SheetAllocationTypes } from '@/store/types'

export default {
  name: 'OptimiserSheetPrintDialog',
  mixins: [
    modalDialog,
    form
  ],
  components: {
    ModalDialog,
  },
  props: {
    productionStationId: Number,
    optimiserSheetId: Number
  },
  data() {
    return {
      sheetData: {},
      panelCount: 0,
      offcutCount: 0,
      panelLabelPrintData: {},
      offcutLabelPrintData: {},
      isPrintingPanels: false,
      isPrintingPanelComplete: false,
      isPrintingOffcuts: false,
      isPrintingOffcutComplete: false
      
    }
  },
  watch: {
  },
  created() {
  },
  mounted() {
  },
  computed: {
    hasPanels() {
      return this.panelCount > 0    
    },
    hasOffcuts() {
      return this.offcutCount > 0
    }
  },
  methods: {
    show(params) {
      this.submitting = false

      this.requestData()

      return modalDialog.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(null)
    },
    async requestData() {
      this.$refs.loadingPanel.startLoading()

      // Note: Panels & Offcuts are split up into 2 seperate downloads
      // Not ideal, but too much work refactoring all the panel label code on the API just for sheet label printing in optimisation
      // Note: Print offcuts 2nd as they'll have cutting indicies set after panels
      try {
        let sheetInfo = await this.$apiService.glassProductionStationService.getOptimiserSheet(this.optimiserSheetId)
        this.sheetData = sheetInfo.data

        this.panelCount = this.sheetData.optimiserSheetAllocations.filter(alloc => alloc.type == SheetAllocationTypes.panel || alloc.type == SheetAllocationTypes.splitPanel).length
        this.offcutCount = this.sheetData.optimiserSheetAllocations.filter(alloc => alloc.type == SheetAllocationTypes.offcut).length

        this.panelLabelPrintData = {}
        this.offcutLabelPrintData = {}

        if(this.panelCount > 0) {
          let panelURLInfo = await this.$apiService.glassProductionStationService.generateCuttingSheetPrintPanelLabelsURL(this.productionStationId, this.optimiserSheetId, { download: true, label: true })
          this.panelLabelPrintData = panelURLInfo.data
        }
        if(this.offcutCount > 0) {
          let offcutURLInfo = await this.$apiService.glassProductionStationService.generateCuttingSheetPrintOffcutLabelsURL(this.productionStationId, this.optimiserSheetId, { download: true, label: true })
          this.offcutLabelPrintData = offcutURLInfo.data
        }

        if(this.panelCount > 0) {
          this.printPanels(true)

          // TODO: Flag dialog timeout in 20s
          // Cancel timeout if a button is clicked
          this.$refs.dialog.startAutoCloseTimer(20000)          

        }
        else if(this.offcutCount > 0) {
          this.printOffcuts()
        }

      } catch(err) {
        console.error(err)
        this.$refs.loadingPanel.setError('Error loading label data', err)
      }

      this.$refs.loadingPanel.finishedLoading()
    },
    printPanels(printOffcuts = false) {
      let dialogComp = this

      this.isPrintingPanels = true
      this.isPrintingPanelComplete = false

      window.location.href = this.panelLabelPrintData.url

      // Wait for the station to process the panel download & then start the offcut download (if requested)
      // This is so hacky, but we have no other feedback for printing & cannot do simultaneous prints
      let panelPrintDuration = Math.max(3000, Math.min(this.panelCount * 750, 15000))
      setTimeout(() => {
        dialogComp.panelPrintCompleted(printOffcuts)
      }, panelPrintDuration);
    },
    panelPrintCompleted(printOffcuts = false) {
      this.isPrintingPanels = false
      this.isPrintingPanelComplete = true

      if(printOffcuts && this.offcutCount > 0) {
        this.printOffcuts()
      }
    },
    printOffcuts() {
      let dialogComp = this

      this.isPrintingOffcuts = true
      this.isPrintingOffcutComplete = false
      window.location.href = this.offcutLabelPrintData.url

      setTimeout(() => {
        dialogComp.offcutPrintCompleted()
      }, 3000);
    },
    offcutPrintCompleted() {
      this.isPrintingOffcuts = false
      this.isPrintingOffcutComplete = true
    },
    cancelDialogTimer() {
      this.$refs.dialog.cancelAutoCloseTimer()
    }
  }
}
</script>